import jsPDF from 'jspdf';
import { isSqmMode, sqmLineDisplay } from './signageSqmUtils';

export const generateGenericQuotationPDF = (quote, logoBase64) => {
  const pdf = new jsPDF();
  const pageWidth = 210;
  const margin = 15;
  const cw = pageWidth - 2 * margin;
  let y = 15;

  // Header with logo
  const spPhone = quote.salesman_phone || '';
  const contactLine = `www.prismoline.com | sales@prismoline.com${spPhone ? ' | +91 ' + spPhone : ''}`;
  pdf.setFillColor(36, 84, 156);
  pdf.rect(0, 0, pageWidth, 38, 'F');
  pdf.setTextColor(255); pdf.setFontSize(18); pdf.setFont(undefined, 'bold');
  pdf.text('SHARDA INFRASOLUTIONS PVT. LTD.', margin, 15);
  pdf.setFontSize(8); pdf.setFont(undefined, 'normal');
  pdf.text('Road Safety & Infrastructure Solutions', margin, 22);
  pdf.text('CIN: U24100JH2010PTC014546 | PAN: AAPCS1562H', margin, 28);
  pdf.text(contactLine, margin, 34);

  if (logoBase64) {
    try { pdf.addImage(logoBase64, 'PNG', pageWidth - margin - 40, 4, 38, 28); } catch (e) { /* */ }
  }

  y = 45;
  pdf.setTextColor(36, 84, 156); pdf.setFontSize(14); pdf.setFont(undefined, 'bold');
  pdf.text('QUOTATION', pageWidth / 2, y, { align: 'center' });
  y = 52; pdf.setDrawColor(36, 84, 156); pdf.line(margin, y, pageWidth - margin, y);
  y += 5;

  pdf.setTextColor(100); pdf.setFontSize(8); pdf.setFont(undefined, 'normal');
  pdf.text('Ref:', margin, y); pdf.setTextColor(0); pdf.setFont(undefined, 'bold');
  pdf.text(quote.quotation_number || quote.id?.substring(0, 16) || '', margin + 12, y);
  pdf.setTextColor(100); pdf.setFont(undefined, 'normal');
  pdf.text('Date:', pageWidth / 2, y); pdf.setTextColor(0); pdf.text(quote.date || '', pageWidth / 2 + 14, y);
  y += 6;
  pdf.setTextColor(100); pdf.text('Valid Until:', margin, y); pdf.setTextColor(0); pdf.text(quote.valid_until || '', margin + 22, y);
  pdf.setTextColor(100); pdf.text('Salesman:', pageWidth / 2, y); pdf.setTextColor(0); pdf.text(quote.salesman_name || '', pageWidth / 2 + 22, y);

  y += 10;

  // 3-Box Layout: Supplier | Bill To | Ship To
  const branches = quote._branches || [];
  const selectedBranch = branches.find(b => b.id === quote.branch);
  const hasShipTo = quote.ship_to_name || quote.ship_to_address;
  const numBoxes = hasShipTo ? 3 : 2;
  const boxWidth = numBoxes === 3 ? (cw - 6) / 3 : (cw - 3) / 2;
  const gap = 3;

  const branchAddr = selectedBranch ? [selectedBranch.address, selectedBranch.city, selectedBranch.state, selectedBranch.pincode ? `- ${selectedBranch.pincode}` : ''].filter(Boolean).join(', ') : '';
  const custAddr = [quote.customer_address, quote.customer_city, quote.customer_state].filter(Boolean).join(', ');
  const shipAddr = hasShipTo ? [quote.ship_to_address, quote.ship_to_city, quote.ship_to_state].filter(Boolean).join(', ') : '';

  const branchAddrLines = branchAddr ? pdf.splitTextToSize(branchAddr, boxWidth - 6) : [];
  const custAddrLines = custAddr ? pdf.splitTextToSize(custAddr, boxWidth - 6) : [];
  const shipAddrLines = shipAddr ? pdf.splitTextToSize(shipAddr, boxWidth - 6) : [];
  // Wrap bold party names too — long names must never bleed into the next box
  pdf.setFont(undefined, 'bold'); pdf.setFontSize(8);
  const custNameLines = pdf.splitTextToSize(quote.customer_name || '-', boxWidth - 6);
  const shipNameLines = hasShipTo ? pdf.splitTextToSize(quote.ship_to_name || '', boxWidth - 6) : [];
  pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
  const nameExtraLines = Math.max(custNameLines.length, shipNameLines.length, 1) - 1;
  const maxLines = Math.max(branchAddrLines.length, custAddrLines.length, shipAddrLines.length);
  const boxHeight = Math.max(28, 18 + maxLines * 3.5 + 6 + nameExtraLines * 4);

  const box1X = margin;
  const box2X = margin + boxWidth + gap;
  const box3X = margin + 2 * (boxWidth + gap);

  // Box 1: Supplier
  pdf.setFillColor(245, 247, 250); pdf.rect(box1X, y - 3, boxWidth, boxHeight, 'F');
  pdf.setTextColor(36, 84, 156); pdf.setFont(undefined, 'bold'); pdf.setFontSize(7);
  pdf.text('SUPPLIER', box1X + 3, y + 2);
  pdf.setTextColor(0); pdf.setFont(undefined, 'bold'); pdf.setFontSize(8);
  pdf.text('Sharda Infrasolutions Pvt Ltd', box1X + 3, y + 8);
  pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
  if (selectedBranch?.name) { pdf.text(selectedBranch.name, box1X + 3, y + 13); }
  let bY = y + 17;
  branchAddrLines.forEach((line, i) => { pdf.text(line, box1X + 3, bY + i * 3.5); });
  bY += branchAddrLines.length * 3.5;
  if (selectedBranch?.gstin) { pdf.text(`GSTIN: ${selectedBranch.gstin}`, box1X + 3, bY); }

  // Box 2: Bill To
  pdf.setFillColor(245, 247, 250); pdf.rect(box2X, y - 3, boxWidth, boxHeight, 'F');
  pdf.setTextColor(36, 84, 156); pdf.setFont(undefined, 'bold'); pdf.setFontSize(7);
  pdf.text('BILL TO', box2X + 3, y + 2);
  pdf.setTextColor(0); pdf.setFont(undefined, 'bold'); pdf.setFontSize(8);
  custNameLines.forEach((line, i) => pdf.text(line, box2X + 3, y + 8 + i * 4));
  pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
  let cY = y + 13 + (custNameLines.length - 1) * 4;
  custAddrLines.forEach((line, i) => { pdf.text(line, box2X + 3, cY + i * 3.5); });
  cY += custAddrLines.length * 3.5;
  if (quote.customer_gstin) { pdf.text(`GSTIN: ${quote.customer_gstin}`, box2X + 3, cY); }

  // Box 3: Ship To
  if (hasShipTo) {
    pdf.setFillColor(245, 247, 250); pdf.rect(box3X, y - 3, boxWidth, boxHeight, 'F');
    pdf.setTextColor(36, 84, 156); pdf.setFont(undefined, 'bold'); pdf.setFontSize(7);
    pdf.text('SHIP TO', box3X + 3, y + 2);
    pdf.setTextColor(0); pdf.setFont(undefined, 'bold'); pdf.setFontSize(8);
    shipNameLines.forEach((line, i) => pdf.text(line, box3X + 3, y + 8 + i * 4));
    pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
    let sY = y + 13 + (shipNameLines.length - 1) * 4;
    shipAddrLines.forEach((line, i) => { pdf.text(line, box3X + 3, sY + i * 3.5); });
  }

  y += boxHeight + 6;

  // Items Table
  pdf.setFillColor(36, 84, 156); pdf.rect(margin, y, cw, 7, 'F');
  pdf.setTextColor(255); pdf.setFont(undefined, 'bold'); pdf.setFontSize(7);
  const cols = [margin + 2, margin + 10, margin + 62, margin + 82, margin + 98, margin + 113, margin + 130, margin + 150, margin + 165];
  ['#', 'Product', 'HSN', 'Qty', 'Unit', 'Rate', 'Disc%', 'Amount'].forEach((h, i) => pdf.text(h, cols[i], y + 5));

  y += 11; // Extra gap between header and first item
  pdf.setTextColor(0); pdf.setFont(undefined, 'normal'); pdf.setFontSize(8);
  // Compute actual line heights from jsPDF internals so zebra/separator never overlap text
  const BODY_LH = (pdf.getLineHeight ? pdf.getLineHeight() : 8 * 1.15) / pdf.internal.scaleFactor;
  const DESC_LH = (6.5 * 1.15) / pdf.internal.scaleFactor;
  const ROW_PAD_TOP = 3.0;
  const ROW_PAD_BOT = 3.0;
  const PRODUCT_COL_WIDTH = 48;

  (quote.items || []).filter(item => (item.name && item.name.trim()) || item.product || Number(item.qty) > 0 || Number(item.rate) > 0).forEach((item, idx) => {
    // Pre-measure row
    const nameText = item.name || '';
    const nameLines = pdf.splitTextToSize(nameText, PRODUCT_COL_WIDTH);
    pdf.setFontSize(6.5);
    const descLines = item.description ? pdf.splitTextToSize(`Description: ${item.description}`, PRODUCT_COL_WIDTH) : [];
    pdf.setFontSize(8);
    const textH = nameLines.length * BODY_LH + descLines.length * DESC_LH + (descLines.length > 0 ? 1 : 0);
    const rowH = Math.max(8, textH + ROW_PAD_TOP + ROW_PAD_BOT);

    // Page break BEFORE drawing if row won't fit
    if (y + rowH > 272) { pdf.addPage(); y = 15; }
    // Zebra sized to actual row
    if (idx % 2 === 0) { pdf.setFillColor(248, 249, 252); pdf.rect(margin, y, cw, rowH, 'F'); }

    pdf.setTextColor(0); pdf.setFontSize(8);
    const baseY = y + ROW_PAD_TOP;
    pdf.text(`${idx + 1}`, cols[0], baseY);
    pdf.text(nameLines, cols[1], baseY);          // Multi-line product name
    pdf.text(item.hsn_code || '-', cols[2], baseY);
    pdf.text(`${item.qty}`, cols[3], baseY);
    pdf.text(item.unit || '', cols[4], baseY);
    pdf.text(`${(item.rate || 0).toLocaleString('en-IN')}`, cols[5], baseY);
    pdf.text(`${item.discount || 0}%`, cols[6], baseY);
    pdf.text(`${(item.amount || 0).toLocaleString('en-IN')}`, cols[7], baseY);

    // Description (smaller font) below product name
    if (descLines.length > 0) {
      pdf.setFontSize(6.5); pdf.setTextColor(100);
      pdf.text(descLines, cols[1], baseY + nameLines.length * BODY_LH + 1);
      pdf.setFontSize(8); pdf.setTextColor(0);
    }

    y += rowH;
    pdf.setDrawColor(220); pdf.line(margin, y, pageWidth - margin, y);
  });

  y += 3; pdf.setDrawColor(36, 84, 156); pdf.setLineWidth(0.5); pdf.line(margin, y, pageWidth - margin, y); pdf.setLineWidth(0.2); y += 6;
  pdf.setFontSize(8);
  pdf.text('Subtotal:', cols[6], y); pdf.setFont(undefined, 'bold'); pdf.text(`INR ${(quote.subtotal || 0).toLocaleString('en-IN')}`, cols[7], y); y += 6;
  pdf.setFont(undefined, 'normal');
  // Other charges (freight, packing, etc) — between subtotal and GST
  (quote.other_charges || []).forEach(ch => {
    const amt = Number(ch.amount) || 0;
    if (!amt) return;
    const label = `${ch.description || 'Other Charge'}${ch.taxable !== false ? ' (+GST)' : ''}:`;
    pdf.text(label, cols[6], y); pdf.text(`INR ${amt.toLocaleString('en-IN')}`, cols[7], y); y += 6;
  });
  pdf.text(`GST (${quote.gst_rate}%):`, cols[6], y); pdf.text(`INR ${(quote.gst_amount || 0).toLocaleString('en-IN')}`, cols[7], y); y += 6;
  pdf.setFillColor(36, 84, 156); pdf.rect(cols[5], y - 4, pageWidth - margin - cols[5], 8, 'F');
  pdf.setTextColor(255); pdf.setFont(undefined, 'bold'); pdf.setFontSize(9);
  pdf.text('Total:', cols[6], y + 1); pdf.text(`INR ${(quote.total || 0).toLocaleString('en-IN')}`, cols[7], y + 1);

  y += 14; pdf.setTextColor(36, 84, 156); pdf.setFontSize(9); pdf.setFont(undefined, 'bold');
  pdf.text('Terms & Conditions', margin, y); y += 6;
  pdf.setTextColor(60); pdf.setFontSize(7); pdf.setFont(undefined, 'normal');
  const terms = quote.terms || {};
  Object.entries(terms).forEach(([key, val]) => {
    if (!val) return;
    const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    pdf.setFont(undefined, 'bold'); pdf.text(`${label}: `, margin, y);
    pdf.setFont(undefined, 'normal');
    const lines = pdf.splitTextToSize(String(val), cw - 35);
    lines.forEach((line, li) => { pdf.text(line, margin + 33, y + (li * 4)); });
    y += Math.max(5, lines.length * 4 + 1);
    if (y > 280) { pdf.addPage(); y = 15; }
  });

  y = Math.max(y + 10, 275);
  pdf.setDrawColor(36, 84, 156); pdf.line(margin, y, pageWidth - margin, y); y += 4;
  pdf.setFontSize(7); pdf.setTextColor(120);
  pdf.text('This is a system-generated Quotation from Prismoline ERP.', pageWidth / 2, y, { align: 'center' });
  pdf.save(`Quotation_${quote.quotation_number || (quote.id || 'draft').substring(0, 12)}.pdf`);
};

export const generateSignagePDFFromQuote = (quote, branches, logoBase64) => {
  const pdf = new jsPDF();
  const pw = 210; const m = 15; const cw = pw - 2 * m; let y = 15;
  const sp = quote.signage_project || {};
  const selectedBranch = branches.find(b => b.id === quote.branch);

  const spPhone = quote.salesman_phone || '';
  const contactLine = `www.prismoline.com | sales@prismoline.com${spPhone ? ' | +91 ' + spPhone : ''}`;
  pdf.setFillColor(36, 84, 156); pdf.rect(0, 0, pw, 38, 'F');
  pdf.setTextColor(255); pdf.setFontSize(18); pdf.setFont(undefined, 'bold');
  pdf.text('SHARDA INFRASOLUTIONS PVT. LTD.', m, 14);
  pdf.setFontSize(8); pdf.setFont(undefined, 'normal');
  pdf.text('Road Safety & Infrastructure Solutions', m, 22);
  pdf.setFontSize(7);
  pdf.text('CIN: U24100JH2010PTC014546 | PAN: AAPCS1562H', m, 28);
  pdf.text(contactLine, m, 34);

  if (logoBase64) {
    try { pdf.addImage(logoBase64, 'PNG', pw - m - 40, 4, 38, 28); } catch (e) { /* */ }
  }

  y = 46;
  pdf.setTextColor(36, 84, 156); pdf.setFontSize(14); pdf.setFont(undefined, 'bold');
  pdf.text('SIGNAGE QUOTATION', pw / 2, y, { align: 'center' }); y += 4;
  pdf.setDrawColor(36, 84, 156); pdf.setLineWidth(0.5); pdf.line(m, y, pw - m, y); y += 8;

  // Info section
  pdf.setTextColor(0); pdf.setFontSize(8);
  const leftX = m; const rightX = pw / 2 + 5;
  const infoStartY = y;

  if (quote.quotation_number) { pdf.setFont(undefined, 'bold'); pdf.text('Ref:', leftX, y); pdf.setFont(undefined, 'normal'); pdf.text(quote.quotation_number, leftX + 22, y); y += 5; }
  if (sp.project_name) { pdf.setFont(undefined, 'bold'); pdf.text('Project:', leftX, y); pdf.setFont(undefined, 'normal'); pdf.text(sp.project_name, leftX + 22, y); y += 5; }
  if (sp.stretch_no) { pdf.setFont(undefined, 'bold'); pdf.text('Stretch:', leftX, y); pdf.setFont(undefined, 'normal'); pdf.text(sp.stretch_no, leftX + 22, y); y += 5; }
  pdf.setFont(undefined, 'bold'); pdf.text('Salesman:', leftX, y); pdf.setFont(undefined, 'normal'); pdf.text(quote.salesman_name || '-', leftX + 22, y);

  let ry = infoStartY;
  pdf.setFont(undefined, 'bold'); pdf.text('Date:', rightX, ry); pdf.setFont(undefined, 'normal'); pdf.text(quote.date || '', rightX + 22, ry); ry += 5;
  pdf.setFont(undefined, 'bold'); pdf.text('Valid Until:', rightX, ry); pdf.setFont(undefined, 'normal'); pdf.text(quote.valid_until || '-', rightX + 22, ry); ry += 5;
  // Compute uniform vs mixed work types across signage items
  const _itemWTs = (quote.signage_items || []).map(si => si.work_type || sp.work_type || '').filter(Boolean);
  const _uniqueWTs = Array.from(new Set(_itemWTs));
  const _mixedWT = _uniqueWTs.length > 1;
  const _headerWT = _mixedWT ? '' : (_uniqueWTs[0] || sp.work_type || '');
  if (sp.work_for) {
    pdf.setFont(undefined, 'bold'); pdf.text('Work:', rightX, ry); pdf.setFont(undefined, 'normal');
    pdf.text(_headerWT ? `${sp.work_for} - ${_headerWT}` : sp.work_for, rightX + 22, ry); ry += 5;
  }

  y = Math.max(y, ry) + 8;

  // 3-Box Address Section
  const hasShipTo = quote.ship_to_name || quote.ship_to_address;
  const numBoxes = hasShipTo ? 3 : 2;
  const boxWidth = numBoxes === 3 ? (cw - 6) / 3 : (cw - 3) / 2;
  const gap = 3;

  const branchAddr = selectedBranch ? [selectedBranch.address, selectedBranch.city, selectedBranch.state, selectedBranch.pincode ? `- ${selectedBranch.pincode}` : ''].filter(Boolean).join(', ') : '';
  const custAddr = [quote.customer_address, quote.customer_city, quote.customer_state].filter(Boolean).join(', ');
  const shipAddr = hasShipTo ? [quote.ship_to_address, quote.ship_to_city, quote.ship_to_state].filter(Boolean).join(', ') : '';

  const branchAddrLines = branchAddr ? pdf.splitTextToSize(branchAddr, boxWidth - 6) : [];
  const custAddrLines = custAddr ? pdf.splitTextToSize(custAddr, boxWidth - 6) : [];
  const shipAddrLines = shipAddr ? pdf.splitTextToSize(shipAddr, boxWidth - 6) : [];
  pdf.setFont(undefined, 'bold'); pdf.setFontSize(8);
  const custNameLines = pdf.splitTextToSize(quote.customer_name || '-', boxWidth - 6);
  const shipNameLines = hasShipTo ? pdf.splitTextToSize(quote.ship_to_name || '', boxWidth - 6) : [];
  pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
  const nameExtraLines = Math.max(custNameLines.length, shipNameLines.length, 1) - 1;
  const boxHeight = Math.max(28, 18 + Math.max(branchAddrLines.length, custAddrLines.length, shipAddrLines.length) * 3.5 + 6 + nameExtraLines * 4);

  const box1X = m; const box2X = m + boxWidth + gap; const box3X = m + 2 * (boxWidth + gap);

  pdf.setFillColor(245, 247, 250); pdf.rect(box1X, y - 3, boxWidth, boxHeight, 'F');
  pdf.setTextColor(36, 84, 156); pdf.setFont(undefined, 'bold'); pdf.setFontSize(7);
  pdf.text('SUPPLIER', box1X + 3, y + 2);
  pdf.setTextColor(0); pdf.setFont(undefined, 'bold'); pdf.setFontSize(8);
  pdf.text('Sharda Infrasolutions Pvt Ltd', box1X + 3, y + 8);
  pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
  if (selectedBranch?.name) { pdf.text(selectedBranch.name, box1X + 3, y + 13); }
  let bY = y + 17;
  branchAddrLines.forEach((line, i) => { pdf.text(line, box1X + 3, bY + i * 3.5); });
  bY += branchAddrLines.length * 3.5;
  if (selectedBranch?.gstin) { pdf.text(`GSTIN: ${selectedBranch.gstin}`, box1X + 3, bY); }

  pdf.setFillColor(245, 247, 250); pdf.rect(box2X, y - 3, boxWidth, boxHeight, 'F');
  pdf.setTextColor(36, 84, 156); pdf.setFont(undefined, 'bold'); pdf.setFontSize(7);
  pdf.text('BILL TO', box2X + 3, y + 2);
  pdf.setTextColor(0); pdf.setFont(undefined, 'bold'); pdf.setFontSize(8);
  custNameLines.forEach((line, i) => pdf.text(line, box2X + 3, y + 8 + i * 4));
  pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
  let cY = y + 13 + (custNameLines.length - 1) * 4;
  custAddrLines.forEach((line, i) => { pdf.text(line, box2X + 3, cY + i * 3.5); });
  cY += custAddrLines.length * 3.5;
  if (quote.customer_gstin) { pdf.text(`GSTIN: ${quote.customer_gstin}`, box2X + 3, cY); }

  if (hasShipTo) {
    pdf.setFillColor(245, 247, 250); pdf.rect(box3X, y - 3, boxWidth, boxHeight, 'F');
    pdf.setTextColor(36, 84, 156); pdf.setFont(undefined, 'bold'); pdf.setFontSize(7);
    pdf.text('SHIP TO', box3X + 3, y + 2);
    pdf.setTextColor(0); pdf.setFont(undefined, 'bold'); pdf.setFontSize(8);
    shipNameLines.forEach((line, i) => pdf.text(line, box3X + 3, y + 8 + i * 4));
    pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
    let sY = y + 13 + (shipNameLines.length - 1) * 4;
    shipAddrLines.forEach((line, i) => { pdf.text(line, box3X + 3, sY + i * 3.5); });
  }

  y += boxHeight + 6;

  // Items Table — Columns mirror OC Signage PDF: # | Description | Shape | Dimensions | Qty | Rate/Board | Amount
  const colX = { num: m + 2, desc: m + 8, shape: m + 78, dims: m + 98, qty: m + 120, rate: m + 137, amount: m + 158 };
  const descColWidth = 68;

  pdf.setFillColor(36, 84, 156); pdf.rect(m, y, cw, 7, 'F');
  pdf.setTextColor(255); pdf.setFontSize(7.5); pdf.setFont(undefined, 'bold');
  ['#', 'Description', 'Shape', 'Dimensions', 'Qty', 'Rate', 'Amount'].forEach((h, i) => {
    pdf.text(h, [colX.num, colX.desc, colX.shape, colX.dims, colX.qty, colX.rate, colX.amount][i], y + 5);
  });
  y += 7;

  pdf.setTextColor(0); pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
  // Actual jsPDF line-height — prevents row separator overlapping the last text line
  const Q_LINE_H = (pdf.getLineHeight ? pdf.getLineHeight() : 7 * 1.15) / pdf.internal.scaleFactor;
  const Q_PAD_TOP = 3.5;
  const Q_PAD_BOT = 3.5;
  const SPEC_LINE_H = (6.5 * 1.15) / pdf.internal.scaleFactor; // spec-desc rendered in 6.5pt

  (quote.signage_items || []).forEach((item, idx) => {
    const isPcs = item.pricing_mode === 'per_pcs';
    const displayRate = isPcs ? (item.custom_per_pcs_rate || item.final_per_board) : item.final_per_board;
    const displayTotal = item.total_amount || 0;

    // Build description column content: board_description + [work_type tag if mixed] + spec description (separate font)
    const rowWT = item.work_type || sp.work_type || '';
    const wtTag = (_mixedWT && rowWT) ? `[${rowWT}]` : '';
    const boardDesc = item.description || '-';
    const mainLines = pdf.splitTextToSize(wtTag ? `${boardDesc}\n${wtTag}` : boardDesc, descColWidth);

    // Precompute spec-description lines (rendered in smaller font below main desc)
    pdf.setFontSize(6.5);
    const specDescRaw = item.specific_description || '';
    const itemDisc = item.discount_amount || 0;
    const discNote = itemDisc > 0
      ? (item.disc_type === 'percent'
          ? `Discount: ${(item.disc_value || 0)}% (-INR ${Math.round(itemDisc).toLocaleString('en-IN')})`
          : `Discount: -INR ${Math.round(itemDisc).toLocaleString('en-IN')}`)
      : '';
    const specCombined = [specDescRaw ? `Description: ${specDescRaw}` : '', discNote].filter(Boolean).join('  •  ');
    const specLines = specCombined ? pdf.splitTextToSize(specCombined, descColWidth) : [];
    pdf.setFontSize(7);

    const textH = mainLines.length * Q_LINE_H + specLines.length * SPEC_LINE_H + (specLines.length > 0 ? 1 : 0);
    const rowH = Math.max(8, textH + Q_PAD_TOP + Q_PAD_BOT);

    if (y + rowH > 272) { pdf.addPage(); y = 15; }
    if (idx % 2 === 1) { pdf.setFillColor(248, 249, 252); pdf.rect(m, y, cw, rowH, 'F'); }

    pdf.setTextColor(0);
    const baseY = y + Q_PAD_TOP;
    pdf.setFontSize(7);
    pdf.text(`${idx + 1}`, colX.num, baseY);
    pdf.text(mainLines, colX.desc, baseY);
    pdf.text(`${item.shape || '-'}`, colX.shape, baseY);
    pdf.text(`${item.dimensions || '-'}`, colX.dims, baseY);
    if (isSqmMode(item)) {
      const { totalSqm, ratePerSqm } = sqmLineDisplay(item, item.qty, displayTotal);
      pdf.text(`${totalSqm} SQM`, colX.qty, baseY);
      pdf.text(`INR ${ratePerSqm.toLocaleString('en-IN')}`, colX.rate, baseY);
    } else {
      pdf.text(`${item.qty || 0}`, colX.qty, baseY);
      pdf.text(`INR ${Math.round(displayRate || 0).toLocaleString('en-IN')}`, colX.rate, baseY);
    }
    pdf.text(`INR ${Math.round(displayTotal).toLocaleString('en-IN')}`, colX.amount, baseY);

    // Spec description rendered under the main description
    if (specLines.length > 0) {
      pdf.setFontSize(6.5); pdf.setTextColor(100);
      const specStartY = baseY + (mainLines.length * Q_LINE_H) + 1;
      pdf.text(specLines, colX.desc, specStartY);
      pdf.setFontSize(7); pdf.setTextColor(0);
    }

    y += rowH;
    pdf.setDrawColor(220); pdf.line(m, y, m + cw, y);
  });

  // Additional (normal) products in a signage quotation
  const normalItemsQ = quote.items || [];
  if (normalItemsQ.length > 0) {
    if (y + 14 > 272) { pdf.addPage(); y = 15; }
    pdf.setFillColor(240, 243, 248); pdf.rect(m, y, cw, 6, 'F');
    pdf.setTextColor(36, 84, 156); pdf.setFontSize(7); pdf.setFont(undefined, 'bold');
    pdf.text('Additional Products', colX.num, y + 4);
    y += 6;
    pdf.setTextColor(0); pdf.setFont(undefined, 'normal'); pdf.setFontSize(7);
    normalItemsQ.forEach((it, ni) => {
      const nameLines = pdf.splitTextToSize(`${it.name || '-'}${it.description ? '\n' + it.description : ''}`, descColWidth);
      const rowH = Math.max(8, nameLines.length * Q_LINE_H + Q_PAD_TOP + Q_PAD_BOT);
      if (y + rowH > 272) { pdf.addPage(); y = 15; }
      if (ni % 2 === 1) { pdf.setFillColor(248, 249, 252); pdf.rect(m, y, cw, rowH, 'F'); }
      const bY = y + Q_PAD_TOP;
      pdf.setTextColor(0); pdf.setFontSize(7);
      pdf.text(`${(quote.signage_items || []).length + ni + 1}`, colX.num, bY);
      pdf.text(nameLines, colX.desc, bY);
      pdf.text(`${it.hsn_code || '-'}`, colX.shape, bY);
      pdf.text(`${it.qty || 0} ${it.unit || ''}`, colX.qty, bY);
      pdf.text(`INR ${Math.round(it.rate || 0).toLocaleString('en-IN')}`, colX.rate, bY);
      pdf.text(`INR ${Math.round(it.amount || 0).toLocaleString('en-IN')}`, colX.amount, bY);
      y += rowH;
      pdf.setDrawColor(220); pdf.line(m, y, m + cw, y);
    });
  }

  y += 3; pdf.setDrawColor(200); pdf.setLineWidth(0.3); pdf.line(m, y, pw - m, y); y += 7;
  const totLabelX = m + 100; const totValX = pw - m - 2;
  pdf.setFontSize(8);

  // Aggregate per-item discounts (stored total_amount is the NET after item discount)
  let itemsGrossQ = 0, itemDiscTotalQ = 0;
  (quote.signage_items || []).forEach(it => {
    const d = it.discount_amount || 0;
    itemsGrossQ += (it.total_amount || 0) + d;
    itemDiscTotalQ += d;
  });
  // Include additional (normal) product amounts in the items total
  (quote.items || []).forEach(it => { itemsGrossQ += (it.amount || 0); });
  const transportQ = Number(sp.transportation_amount) || 0;
  const netItemsQ = itemsGrossQ - itemDiscTotalQ;
  const netTotalQ = (quote.net_total != null && quote.net_total > 0) ? quote.net_total : (netItemsQ + transportQ);
  const overallDiscQ = quote.discount_amount || 0;

  pdf.setFont(undefined, 'normal'); pdf.text('Items Total:', totLabelX, y); pdf.setFont(undefined, 'bold'); pdf.text(`INR ${Math.round(itemsGrossQ).toLocaleString('en-IN')}`, totValX, y, { align: 'right' }); y += 6;
  if (itemDiscTotalQ > 0) { pdf.setFont(undefined, 'normal'); pdf.text('Item Discounts:', totLabelX, y); pdf.text(`- INR ${Math.round(itemDiscTotalQ).toLocaleString('en-IN')}`, totValX, y, { align: 'right' }); y += 6; }
  if (transportQ > 0) { pdf.setFont(undefined, 'normal'); pdf.text('Transport:', totLabelX, y); pdf.text(`INR ${Math.round(transportQ).toLocaleString('en-IN')}`, totValX, y, { align: 'right' }); y += 6; }
  if (itemDiscTotalQ > 0 || overallDiscQ > 0) {
    pdf.setFont(undefined, 'normal'); pdf.text('Net Total:', totLabelX, y); pdf.setFont(undefined, 'bold'); pdf.text(`INR ${Math.round(netTotalQ).toLocaleString('en-IN')}`, totValX, y, { align: 'right' }); y += 6;
  }
  if (overallDiscQ > 0) {
    pdf.setFont(undefined, 'normal'); pdf.text(`Discount${quote.discount_type === 'percent' ? ' (' + (quote.discount_value || 0) + '%)' : ''}:`, totLabelX, y); pdf.text(`- INR ${Math.round(overallDiscQ).toLocaleString('en-IN')}`, totValX, y, { align: 'right' }); y += 6;
  }
  pdf.setFont(undefined, 'normal'); pdf.text(overallDiscQ > 0 ? 'Taxable Value:' : 'Subtotal:', totLabelX, y); pdf.setFont(undefined, 'bold'); pdf.text(`INR ${Math.round(quote.subtotal || 0).toLocaleString('en-IN')}`, totValX, y, { align: 'right' }); y += 6;
  pdf.setFont(undefined, 'normal'); pdf.text(`GST (${quote.gst_rate || 18}%):`, totLabelX, y); pdf.text(`INR ${Math.round(quote.gst_amount || 0).toLocaleString('en-IN')}`, totValX, y, { align: 'right' }); y += 7;
  pdf.setFillColor(36, 84, 156); pdf.rect(totLabelX - 3, y - 5, pw - m - totLabelX + 5, 9, 'F');
  pdf.setTextColor(255); pdf.setFont(undefined, 'bold'); pdf.setFontSize(9);
  pdf.text('Total:', totLabelX, y); pdf.text(`INR ${Math.round(quote.total || 0).toLocaleString('en-IN')}`, totValX, y, { align: 'right' });

  y += 16;
  if (y > 250) { pdf.addPage(); y = 15; }
  pdf.setTextColor(36, 84, 156); pdf.setFontSize(9); pdf.setFont(undefined, 'bold');
  pdf.text('Terms & Conditions', m, y); y += 2;
  pdf.setDrawColor(36, 84, 156); pdf.setLineWidth(0.3); pdf.line(m, y, m + 35, y); y += 5;
  pdf.setTextColor(60); pdf.setFontSize(7); pdf.setFont(undefined, 'normal');
  Object.entries(quote.terms || {}).forEach(([key, val]) => {
    if (!val) return;
    if (y > 277) { pdf.addPage(); y = 15; }
    const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    pdf.setFont(undefined, 'bold'); pdf.text(`${label}:`, m, y);
    pdf.setFont(undefined, 'normal');
    const lines = pdf.splitTextToSize(String(val), cw - 35);
    lines.forEach((line, li) => { pdf.text(line, m + 33, y + (li * 4)); });
    y += Math.max(5, lines.length * 4 + 1);
  });

  const footerY = 287;
  pdf.setDrawColor(36, 84, 156); pdf.setLineWidth(0.3); pdf.line(m, footerY, pw - m, footerY);
  pdf.setFontSize(6.5); pdf.setTextColor(140); pdf.setFont(undefined, 'normal');
  pdf.text('System-generated Quotation from Prismoline ERP', pw / 2, footerY + 4, { align: 'center' });

  pdf.save(`Signage_Quote_${quote.quotation_number || (quote.id || 'draft').substring(0, 12)}.pdf`);
};
